import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import BillingSidebar from "@/components/layout/BillingSidebar";
import BillingHeader from "@/components/layout/BillingHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Clock, AlertTriangle, DollarSign, TrendingUp, Search, Download, Phone, Mail } from "lucide-react";
import { format, differenceInDays, parseISO } from "date-fns";

// CPT code pricing for calculating amounts
const cptPricing: Record<string, number> = {
  "99201": 45,
  "99202": 75,
  "99203": 110,
  "99204": 165,
  "99205": 210,
  "99211": 25,
  "99212": 45,
  "99213": 75,
  "99214": 110,
  "99215": 150,
  "99241": 65,
  "99242": 95,
  "99243": 130,
  "99244": 190,
  "99245": 250,
};

interface AgingBucket {
  label: string;
  range: string;
  minDays: number;
  maxDays: number;
  color: string;
}

const agingBuckets: AgingBucket[] = [
  { label: "Current", range: "0-30", minDays: 0, maxDays: 30, color: "bg-success/10 text-success" },
  { label: "31-60 Days", range: "31-60", minDays: 31, maxDays: 60, color: "bg-warning/10 text-warning" },
  { label: "61-90 Days", range: "61-90", minDays: 61, maxDays: 90, color: "bg-orange-100 text-orange-700" },
  { label: "91-120 Days", range: "91-120", minDays: 91, maxDays: 120, color: "bg-destructive/10 text-destructive" },
  { label: "120+ Days", range: "120+", minDays: 121, maxDays: Infinity, color: "bg-destructive/20 text-destructive" },
];

export default function ARAgingPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBucket, setSelectedBucket] = useState<string>("all");

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["ar-aging-sessions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("telehealth_sessions")
        .select("*")
        .in("billing_status", ["submitted", "pending"])
        .order("scheduled_date", { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });

  const { data: intakeForms = [] } = useQuery({
    queryKey: ["ar-intake-forms"],
    queryFn: async () => {
      const { data, error } = await supabase.from("patients").select("*");
      if (error) throw error;
      return data || [];
    },
  });

  // Calculate aging for each claim
  const agingClaims = sessions.map((session) => {
    const member = intakeForms.find((f) => f.user_id === session.member_id);
    const daysOutstanding = differenceInDays(new Date(), parseISO(session.scheduled_date));
    const bucket = agingBuckets.find(
      (b) => daysOutstanding >= b.minDays && daysOutstanding <= b.maxDays
    ) || agingBuckets[agingBuckets.length - 1];
    const amount = session.cpt_code ? (cptPricing[session.cpt_code] || 100) : 100;

    return {
      ...session,
      memberName: member ? `${member.first_name} ${member.last_name}` : "Unknown",
      insuranceType: member?.insurance_type || "Unknown",
      daysOutstanding,
      bucket,
      amount,
    };
  });

  // Calculate bucket summaries
  const bucketSummaries = agingBuckets.map((bucket) => {
    const claims = agingClaims.filter((c) => c.bucket.label === bucket.label);
    const totalAmount = claims.reduce((sum, c) => sum + c.amount, 0);
    return {
      ...bucket,
      count: claims.length,
      totalAmount,
    };
  });

  const totalAR = bucketSummaries.reduce((sum, b) => sum + b.totalAmount, 0);
  const totalClaims = bucketSummaries.reduce((sum, b) => sum + b.count, 0);
  const avgDaysOutstanding = agingClaims.length > 0
    ? Math.round(agingClaims.reduce((sum, c) => sum + c.daysOutstanding, 0) / agingClaims.length)
    : 0;

  // Filter claims
  const filteredClaims = agingClaims.filter((claim) => {
    const matchesSearch =
      claim.memberName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBucket = selectedBucket === "all" || claim.bucket.label === selectedBucket;
    return matchesSearch && matchesBucket;
  });

  const exportToCSV = () => {
    const headers = ["Claim ID", "Member", "Service Date", "Days Outstanding", "Amount", "Insurance", "Bucket"];
    const rows = filteredClaims.map((claim) => [
      claim.id,
      claim.memberName,
      format(parseISO(claim.scheduled_date), "MM/dd/yyyy"),
      claim.daysOutstanding,
      claim.amount.toFixed(2),
      claim.insuranceType,
      claim.bucket.label,
    ]);

    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ar-aging-report-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
  };

  return (
    <div className="flex min-h-screen bg-background">
      <BillingSidebar />
      <div className="flex-1 flex flex-col">
        <BillingHeader />
        <main className="flex-1 p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-display font-bold text-foreground">A/R Aging</h1>
              <p className="text-muted-foreground">Track outstanding accounts receivable by age</p>
            </div>
            <Button onClick={exportToCSV} variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Export Report
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-success/10">
                    <DollarSign className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total A/R</p>
                    <p className="text-2xl font-bold">${totalAR.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Days Outstanding</p>
                    <p className="text-2xl font-bold">{avgDaysOutstanding} days</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-warning/10">
                    <AlertTriangle className="h-5 w-5 text-warning" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Claims Over 60 Days</p>
                    <p className="text-2xl font-bold">
                      {bucketSummaries.filter((b) => b.minDays > 60).reduce((sum, b) => sum + b.count, 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted">
                    <TrendingUp className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Claims</p>
                    <p className="text-2xl font-bold">{totalClaims}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Aging Buckets Visual */}
          <Card>
            <CardHeader>
              <CardTitle>Aging Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-4">
                {bucketSummaries.map((bucket) => (
                  <div
                    key={bucket.label}
                    className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      selectedBucket === bucket.label ? "ring-2 ring-primary" : ""
                    }`}
                    onClick={() => setSelectedBucket(bucket.label === selectedBucket ? "all" : bucket.label)}
                  >
                    <Badge className={bucket.color}>{bucket.range} days</Badge>
                    <p className="text-2xl font-bold mt-2">${bucket.totalAmount.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">{bucket.count} claims</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Claims Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Outstanding Claims</CardTitle>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search claims..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  <Select value={selectedBucket} onValueChange={setSelectedBucket}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filter by age" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Ages</SelectItem>
                      {agingBuckets.map((bucket) => (
                        <SelectItem key={bucket.label} value={bucket.label}>
                          {bucket.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : filteredClaims.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No outstanding claims found</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Claim ID</TableHead>
                      <TableHead>Member</TableHead>
                      <TableHead>Service Date</TableHead>
                      <TableHead>Days Outstanding</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Insurance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClaims.map((claim) => (
                      <TableRow key={claim.id}>
                        <TableCell className="font-mono text-xs">
                          {claim.id.slice(0, 8)}...
                        </TableCell>
                        <TableCell className="font-medium">{claim.memberName}</TableCell>
                        <TableCell>{format(parseISO(claim.scheduled_date), "MMM d, yyyy")}</TableCell>
                        <TableCell>
                          <Badge className={claim.bucket.color}>{claim.daysOutstanding} days</Badge>
                        </TableCell>
                        <TableCell className="font-medium">${claim.amount.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {claim.insuranceType?.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">
                            {claim.billing_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" title="Call payer">
                              <Phone className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" title="Send reminder">
                              <Mail className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
}
